import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { validacaoExecucaoCicloStyles } from './ValidacaoExecucaoCiclo.style'
import { ExecucaoCiclo, ObterExecucoesCicloApiQuery, TipoEnvioCiclo } from '../../types/execucaoCicloTypes'
import { Paginacao } from '../../types/tabelaTypes'
import automacaoService from '../../services/api/automacao/automacaoService'
import { ResponsePaginado } from '../../types/genericoType'

export function validacaoExecucaoCicloIO() {
  const { parceiroId } = useParams<{ parceiroId: string }>()
  const [execucoesCiclo, setExecucoesCiclo] = useState<ResponsePaginado<ExecucaoCiclo>>()
  const [totalExecucoes, setTotalExecucoes] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [modalConfirmacao, setModalConfirmacao] = useState<{
    open: boolean
    execucao: ExecucaoCiclo | null
    tipoAcao: 'aceitar' | 'rejeitar' | null
  }>({
    open: false,
    execucao: null,
    tipoAcao: null
  })

  const buscarExecucoesCiclo = useCallback(
    (paginacao: Paginacao) => {
      setLoading(true)
      const query: ObterExecucoesCicloApiQuery = {
        parceiroId: Number(parceiroId),
        pageNumber: paginacao.pageNumber ?? 1,
        pageSize: paginacao.pageSize ?? 10
      }

      automacaoService
        .listarExecucoesCicloApi(query)
        .then((response) => {
          setExecucoesCiclo(response)
          setTotalExecucoes(response.totalItens)
        })
        .catch((err) => {
          toast.error(err.message)
        })
        .finally(() => setLoading(false))
    },
    [parceiroId]
  )

  useEffect(() => {
    if (parceiroId) {
      buscarExecucoesCiclo({ pageNumber: 1, pageSize: 10 })
    }
  }, [parceiroId, buscarExecucoesCiclo])

  const handleAceitar = useCallback((execucao: ExecucaoCiclo) => {
    setModalConfirmacao({
      open: true,
      execucao,
      tipoAcao: 'aceitar'
    })
  }, [])

  const handleRejeitar = useCallback((execucao: ExecucaoCiclo) => {
    setModalConfirmacao({
      open: true,
      execucao,
      tipoAcao: 'rejeitar'
    })
  }, [])

  const fecharModalConfirmacao = useCallback(() => {
    setModalConfirmacao({
      open: false,
      execucao: null,
      tipoAcao: null
    })
  }, [])

  const confirmarAcao = useCallback(
    async (cicloId: number, envio?: TipoEnvioCiclo) => {
      if (!modalConfirmacao.execucao || !modalConfirmacao.tipoAcao) return

      setLoading(true)
      await automacaoService
        .aprovarValidacaoCicloApi({
          parceiroId: Number(parceiroId),
          funcionarioId: 0,
          cicloId: cicloId,
          envio: Number(envio ?? modalConfirmacao.execucao.tipoEnvio),
          aprovar: modalConfirmacao.tipoAcao === 'aceitar',
          skip: false
        })
        .then((response) => {
          const mensagem = Array.isArray(response.messages) ? response.messages.join(' ') : String(response.messages ?? '')
          if (mensagem.trim()) toast.success(mensagem)
          buscarExecucoesCiclo({ pageNumber: 1, pageSize: 10 })
        })
        .catch((err) => {
          toast.error(err.message)
        })
        .finally(() => {
          fecharModalConfirmacao()
          setLoading(false)
        })
    },
    [modalConfirmacao, fecharModalConfirmacao, parceiroId, buscarExecucoesCiclo]
  )

  return {
    styles: validacaoExecucaoCicloStyles,
    execucoesCiclo,
    totalExecucoes,
    loading,
    modalConfirmacao,
    buscarExecucoesCiclo,
    handleAceitar,
    handleRejeitar,
    fecharModalConfirmacao,
    confirmarAcao
  }
}

export type ValidacaoExecucaoCicloIO = ReturnType<typeof validacaoExecucaoCicloIO>
